import { useTranslation } from '@/hooks/useTranslation';
import { useDateTimeFromIsoString } from '@/hooks/useDateTimeFromIsoString';
import { LicenseEditor } from '@/components/admin/company/LicenseEditor';
import { DeleteCompanyButton } from '@/components/admin/company/DeleteCompanyButton';
import { HydratedConsultingCompany } from '@/slices/consultingCompany/consultingCompany.types';
import { HydratedEnterprise } from '@/slices/enterprises/enterprise.types';

enum ProjectSectionEnum {
  Projects = 'projects',
  ArchivedProjects = 'archived_projects',
  Templates = 'templates',
}

export function CompanyProjectsSections({
  company,
}: {
  company: HydratedConsultingCompany;
}) {
  return (
    <>
      <CompanyProjects
        company={company}
        projectSection={ProjectSectionEnum.Projects}
      />
      <CompanyProjects
        company={company}
        projectSection={ProjectSectionEnum.Templates}
      />
      <CompanyProjects
        company={company}
        projectSection={ProjectSectionEnum.ArchivedProjects}
      />
    </>
  );
}

function CompanyProjects({
  company,
  projectSection,
}: {
  company: HydratedConsultingCompany;
  projectSection: ProjectSectionEnum;
}) {
  const { t } = useTranslation();

  const sectionTitle = t(`admin:companies.list.item.${projectSection}`);

  const projects: HydratedEnterprise[] = (
    company.projectsResolved ?? []
  ).filter((project) => {
    switch (projectSection) {
      case ProjectSectionEnum.Projects:
        return !project.isTemplate && !project.archivedDate;
      case ProjectSectionEnum.Templates:
        return project.isTemplate;
      case ProjectSectionEnum.ArchivedProjects:
        return !project.isTemplate && Boolean(project.archivedDate);
      default:
        return false;
    }
  });

  return (
    <div className="pt-4">
      <div className="flex items-center justify-between gap-1">
        <div className="flex items-center gap-1">
          <h5>{sectionTitle}</h5>
          <div className="tag gray">{projects.length}</div>
        </div>
        {projectSection === ProjectSectionEnum.Projects && (
          <LicenseEditor company={company} />
        )}
      </div>
      <ul className="space-y-1 divide-y divide-gray-100 text-sm w-full">
        {projects.map((project) => (
          <CompanyProject
            key={project.id}
            project={project}
            showDeleteButton={projectSection !== ProjectSectionEnum.Templates}
          />
        ))}
      </ul>
    </div>
  );
}

function CompanyProject({
  project,
  showDeleteButton,
}: {
  project: HydratedEnterprise;
  showDeleteButton: boolean;
}) {
  const { t } = useTranslation();
  const createdBy = project.createdByResolved;
  const createdByLabel = `${createdBy?.firstName} ${createdBy?.lastName}`;
  const createdAt = useDateTimeFromIsoString(project.createdAt);

  return (
    <li className="flex items-center justify-between gap-2 w-full pt-1">
      <div className="space-y-1">
        <div className="font-bold">{project.name}</div>
        <div className="text-gray-500 text-xs">
          {t('admin:companies.list.item.createdAtAndBy', {
            createdAt: createdAt.date,
            createdBy: createdByLabel,
          })}
        </div>
      </div>
      {showDeleteButton && (
        <div className="flex items-center gap-0.5">
          <DeleteCompanyButton companyId={project.id} />
        </div>
      )}
    </li>
  );
}
